"use client";

import { useEffect, useState, Suspense, useRef } from "react";
import { IconX } from "@tabler/icons-react";
import { useAtom } from "jotai/react";

import { useOnClickOutside } from "@/src/hooks/useOnClickOutside";
import type { IDynamicModalAtom } from "@/src/stores/type";
import { cn } from "@/src/helpers/tailwindHelper";
import { dynamicModalAtom } from "@/src/stores";

export const XModal: React.FC = () => {
  const modalRef = useRef<HTMLDivElement | null>(null);

  const [isModal, setIsModal] = useState({ visible: false, active: false });
  const [dynamicModal, setDynamicModal] =
    useAtom<IDynamicModalAtom>(dynamicModalAtom);

  useEffect(() => {
    if (dynamicModal.visible) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  }, [dynamicModal.visible]);

  useEffect(() => {
    if (dynamicModal.visible) {
      setIsModal({ visible: true, active: false });
      setTimeout(() => {
        setIsModal({ visible: true, active: true });
      }, 50);
    } else {
      setIsModal((prev) => ({ ...prev, active: false }));
      setTimeout(() => {
        setIsModal((prev) => ({ ...prev, visible: false }));
      }, 300);
    }
  }, [dynamicModal.visible]);

  useOnClickOutside(modalRef as React.RefObject<HTMLDivElement>, () => {
    if (dynamicModal?.isDismissDisabled) return;
    setDynamicModal((prev) => ({ ...prev, visible: false }));
    dynamicModal.onClose?.();
  });

  return (
    <div className="z-modal relative" data-testid="x-modal">
      {isModal.visible && (
        <Suspense fallback={null}>
          <div
            className={cn(
              "fixed inset-0 flex h-full w-full items-center justify-center bg-black transition-opacity duration-300",
              isModal.active
                ? "pointer-events-none opacity-60 dark:opacity-80"
                : "opacity-0"
            )}
          />

          <div tabIndex={-1}>
            <div
              className={cn(
                "fixed inset-0 mx-auto flex h-full w-[95%] items-center justify-center py-2 transition-all duration-300",
                isModal.active
                  ? "translate-y-0 opacity-100"
                  : "translate-y-1 opacity-0"
              )}>
              <div
                className={cn(
                  "flex max-h-full w-[35rem] max-w-full flex-col overflow-hidden rounded-md bg-white dark:bg-neutral-950"
                )}
                ref={modalRef}>
                <div className="flex items-center justify-between border-b border-slate-200 p-4 dark:border-zinc-800">
                  <div className="text-base-black dark:text-base-white text-lg leading-7 font-semibold">
                    {dynamicModal.title}
                  </div>
                  <IconX
                    onClick={() => {
                      setDynamicModal((prev) => ({
                        ...prev,
                        visible: false,
                      }));
                      dynamicModal.onClose?.();
                    }}
                    className="size-5 cursor-pointer hover:opacity-50"
                  />
                </div>
                <div className="overflow-y-auto px-4 py-3">
                  {dynamicModal.layer}
                </div>
              </div>
            </div>
          </div>
        </Suspense>
      )}
    </div>
  );
};
